import { useMemo, useRef } from 'react';

import { roundOdds } from '../helpers';
import { useSelector } from '../providers/StoreProvider';
import { type Bet, type Odd } from '../types';

import { useBets } from './useBets';

const betTypeHelper = (oddsFormat: string, bestMatchedOdds: Odd) => {
  switch (true) {
    case oddsFormat === 'traditional' || oddsFormat === 'fractional':
      return bestMatchedOdds.fractional;
    case oddsFormat === 'decimal':
      return roundOdds(bestMatchedOdds.decimal);
    case oddsFormat === 'american':
      return bestMatchedOdds.american;
    default:
      return bestMatchedOdds.decimal;
  }
};

export const useBetsWithBestOdds = () => {
  const marketBets = useBets();
  const oddsFormat = useSelector(store => store.oddsFormat);
  const bookmakers = useSelector(store => store.bookmakers);
  const exchangeBookieCodes = useMemo(
    () => (bookmakers || []).filter(bookie => bookie.type.toLowerCase() === 'exchange').map(bookie => bookie.code),
    [bookmakers],
  );

  const exchangeCodesKey = exchangeBookieCodes.join(',');

  const previousResultRef = useRef<Array<
    Bet & { bestOdds: number; bestOddsFractional: string; bestOddsText: string | number }
  > | null>(null);
  const previousKeysRef = useRef<string>('');

  return useMemo(() => {
    const betsKey =
      marketBets?.map(b => `${b.id}:${b.odds.map(o => `${o.bookmakerCode}:${o.decimal}`).join(',')}`).join('|') || '';
    const currentKey = `${betsKey}::${oddsFormat}::${exchangeCodesKey}`;

    if (currentKey === previousKeysRef.current && previousResultRef.current) {
      return previousResultRef.current;
    }

    const result = (marketBets || []).map((bet: Bet) => {
      const filteredOdds = bet.odds.filter(odd => !exchangeBookieCodes.includes(odd.bookmakerCode));

      const bestMatchedOdds = filteredOdds.reduce((prev: Odd, current: Odd) => {
        if (current.status === 'FAILED') return prev;
        return prev.decimal > current.decimal ? prev : current;
      }, {} as Odd);

      return {
        ...bet,
        bestOdds: bestMatchedOdds.decimal,
        bestOddsFractional: bestMatchedOdds.fractional,
        bestOddsText: betTypeHelper(oddsFormat, bestMatchedOdds),
      };
    });

    previousResultRef.current = result;
    previousKeysRef.current = currentKey;
    return result;
  }, [marketBets, oddsFormat, exchangeBookieCodes, exchangeCodesKey]);
};
